import { UpsertMessagePayload } from '@/redux/slices/aiChatSlice'
import { ChunkFlushArgs } from './buffer'
import type AIProcessor from './processor'

export type UTMParams = {
  device: 'mobile' | 'pc' | (string & {}) | undefined
  source: 'gd_next' | (string & {}) | undefined
  medium?: string
}

export type ClientCommand = {
  message: {
    conversation_id: string | undefined
    attachment_ids: string[]
    user_input: string
  } & { [key in keyof UTMParams as `utm_${key}`]: UTMParams[key] } & Record<string, any>
  interrupt: { conversation_id: string | undefined }
}

export enum EventType {
  RunStarted = 'RUN_STARTED',
  RunFinished = 'RUN_FINISHED',
  RunError = 'RUN_ERROR',
  StepStarted = 'STEP_STARTED',
  StepFinished = 'STEP_FINISHED',
  TextMessageStart = 'TEXT_MESSAGE_START',
  TextMessageContent = 'TEXT_MESSAGE_CHUNK',
  TextMessageEnd = 'TEXT_MESSAGE_END',
  ThinkingTextMessageStart = 'THINKING_TEXT_MESSAGE_START',
  ThinkingTextMessageContent = 'THINKING_TEXT_MESSAGE_CONTENT',
  ThinkingTextMessageEnd = 'THINKING_TEXT_MESSAGE_END',
  ToolCallStart = 'TOOL_CALL_START',
  ToolCallArgs = 'TOOL_CALL_ARGS',
  ToolCallEnd = 'TOOL_CALL_END',
  ToolCallResult = 'TOOL_CALL_RESULT',
  Custom = 'CUSTOM',
}

type BaseEvent<Type extends EventType, Props extends object = object, RawEvent = unknown> = {
  type: Type
  timestamp: number
  raw_event: RawEvent
} & Props

export type BaseCustomEvent<Name extends string, ParsedValue = unknown, RawEvent = unknown> = BaseEvent<
  EventType.Custom,
  { name: Name; value: ParsedValue },
  RawEvent
>

export type BaseStepEvent<StepName extends string = string, EndRawEvent = unknown, StartRawEvent = unknown> =
  | BaseEvent<EventType.StepStarted, { step_name: StepName }, StartRawEvent>
  | BaseEvent<EventType.StepFinished, { step_name: StepName }, EndRawEvent>

type RunStartedEvent = BaseEvent<EventType.RunStarted, { thread_id: string; run_id: string }>
type RunFinishedEvent = BaseEvent<EventType.RunFinished, { thread_id: string; run_id: string; result?: unknown }>
type RunErrorEvent = BaseEvent<EventType.RunError, { code?: string; message: string }>

type TextMessageStartEvent = BaseEvent<EventType.TextMessageStart, { message_id: string; role: 'assistant' }>
type TextMessageContentEvent = BaseEvent<
  EventType.TextMessageContent,
  { message_id: string; delta: string },
  { index: number }
>
type TextMessageEndEvent = BaseEvent<EventType.TextMessageEnd, { message_id: string }>
type ThinkingTextMessageStartEvent = BaseEvent<EventType.ThinkingTextMessageStart, { role: 'assistant' }>
type ThinkingTextMessageContentEvent = BaseEvent<
  EventType.ThinkingTextMessageContent,
  { delta: string },
  { index: number }
>
type ThinkingTextMessageEndEvent = BaseEvent<EventType.ThinkingTextMessageEnd>

export type ToolCallStartEvent = BaseEvent<
  EventType.ToolCallStart,
  { tool_call_id: string; tool_call_name: string; parent_message_id?: string }
>
export type ToolCallResultEvent = BaseEvent<
  EventType.ToolCallResult,
  { message_id: string; tool_call_id: string; content: string }
>
export type ToolCallEndEvent = BaseEvent<EventType.ToolCallEnd, { tool_call_id: string }>

export type ParsedToolCallEvents<ToolName extends string = string> =
  | BaseEvent<EventType.ToolCallStart, { tool_call_id: string; tool_call_name: ToolName; parent_message_id?: string }>
  | BaseEvent<EventType.ToolCallEnd, { tool_call_id: string; tool_call_name: ToolName }>

export type CustomEvents = BaseCustomEvent<'content_sensitive', { is_valid: boolean }>

export type RawCustomEvent = BaseEvent<EventType.Custom, { name: CustomEvents['name']; value: string }>

export type ReportDataEvent =
  | RunStartedEvent
  | RunFinishedEvent
  | RunErrorEvent
  | BaseStepEvent
  | TextMessageStartEvent
  | TextMessageContentEvent
  | TextMessageEndEvent
  | ThinkingTextMessageStartEvent
  | ThinkingTextMessageContentEvent
  | ThinkingTextMessageEndEvent
  | ToolCallStartEvent
  | ToolCallEndEvent
  | ToolCallResultEvent
  | RawCustomEvent

// ----- status messages

type ReportStatusCurrentMetadataInput = {
  status: 'current_metadata'

  conversation_id: string
  message_id: string
}

type ReportStatusPaymentRequiredInput = {
  status: 'payment_required'
  detail: string
}

export type ReportStatusMessage = {
  action: 'report_status'
  input: ReportStatusCurrentMetadataInput | ReportStatusPaymentRequiredInput | { status: 'stopped' | 'interrupted' }
}

// ----- data messages

export type ToolName = 'scrape_url' | 'parse_image' | 'parse_pdf' | 'parse_word' | 'parse_excel' | 'parse_ppt'

export type ReportDataMessage = {
  action: 'report_data'
  input: { data: ReportDataEvent }
}

export type ChannelMessage = ReportStatusMessage | ReportDataMessage

// -----

export type AIConnectionPerformFn<ExtraClientCommand extends object> = <A extends keyof ExtraClientCommand>(
  action: A,
  payload: ExtraClientCommand[A]
) => void

export type EventsHandler<ExtraStepEvents extends object, ExtraCustomEvents extends object> = {
  onPrepare?: () => void
  onChunkFlush?: (args: ChunkFlushArgs) => boolean | void
  onStopped?: (interrupted?: true) => boolean | Promise<boolean | void> | void
  onCheckRiskRejected?: () => void
  onExtraStepEvents?: (data: ExtraStepEvents) => void
  onExtraCustomEvents?: (data: ExtraCustomEvents) => void
}

export type EventsHandlerFactory<
  ExtraStepEvents extends object,
  ExtraCustomEvents extends object,
  ExtraClientCommand extends object,
> = (
  processor: AIProcessor<ExtraStepEvents, ExtraCustomEvents, ExtraClientCommand>
) => EventsHandler<ExtraStepEvents, ExtraCustomEvents>

export type AIProcessorOptions = {
  channelName: string
  channelToken: string
  onPerformFail?: () => void
  onMessageStarted?: (backendConversationId: string, backendMessageId: string) => void

  getBackendConversationId?: () => string | undefined
  upsertMessage?: (payload: Omit<UpsertMessagePayload, 'id'>) => void
  reportError?: (error: { step?: string }) => void
}
